import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { useDictionaryData } from '@/components/shared/DictionaryProvider/hooks'
import { EMAIL_REGEXP } from '@/constants/email'

import {
  getEmailModalDefaultValues,
  getPublicDomain,
} from '../../../lib/helpers'
import { Modal, Button, TextInput, TextArea, Spinner } from '../../uikit'
import { Message } from '../Message'
import { ShareLink } from '../ShareLink'

import type { GetEmailInfoPayload } from '../../../api/types/sendToEmployee'
import type { ModalProps } from '../../uikit/Modal/types'

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: auto;
`

type SendToEmployeeModalProps = ModalProps & {
  mailError?: string
}

export const SendToEmployeeModal = ({
  isConsultantForm,
  isLoading,
  hor,
  onSubmit,
  disableSubject,
  mailError,
  ...props
}: SendToEmployeeModalProps) => {
  const { dictionary } = useDictionaryData()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<GetEmailInfoPayload>()

  useEffect(() => {
    if (hor && props.isOpen) {
      const initialValues = getEmailModalDefaultValues({
        hor,
        personInfoKey: 'personalInfo',
      })
      setValue('employeeName', initialValues.employeeName)
      setValue('employeeEmail', initialValues.employeeEmail)
      setValue('subject', initialValues.subject)
      setValue('messageFromRmc', initialValues.messageFromRmc)
    }
  }, [hor, setValue, props.isOpen])

  const link = `${getPublicDomain(
    hor?.company?.slug?.toLowerCase()
  )}/housing-options/${hor?.hash}`
  const employeeName = dictionary.employee
  const consultantName = dictionary.consultant

  return (
    <Modal {...props} title={`Send to ${employeeName}`} withCrossButton>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="send-to-employee-modal"
      >
        {mailError && (
          <Message type="error">Error sending mail: {mailError}</Message>
        )}
        <TextInput
          {...register('employeeName', { required: 'Name is required' })}
          label="Name"
          placeholder={
            isConsultantForm
              ? `Enter ${consultantName}'s name`
              : `Enter ${employeeName}'s name`
          }
          error={errors.employeeName}
        />
        <TextInput
          {...register('employeeEmail', {
            required: 'Email is required',
            pattern: {
              value: EMAIL_REGEXP,
              message: 'Please enter a valid email address',
            },
          })}
          label="Email"
          placeholder={
            isConsultantForm
              ? `Enter ${consultantName}'s email`
              : `Enter ${employeeName}'s email`
          }
          error={errors.employeeEmail}
        />
        <TextInput
          {...register('subject', { required: 'Subject is required' })}
          disabled={disableSubject}
          label="Subject"
          placeholder="Enter the subject of the email"
          error={errors.subject}
        />
        <TextArea
          {...register('messageFromRmc', { required: 'Message is required' })}
          label="Message"
          placeholder="Enter the message of the email"
          error={errors.messageFromRmc}
        />
        <Button
          icon={isLoading && <Spinner />}
          type="submit"
          isFullWidth
          disabled={isLoading}
        >
          Send Email
        </Button>
        <ShareLink link={link} />
      </Form>
    </Modal>
  )
}
